import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Typography,
  message
} from 'antd';
import type { RouteViewModel } from '../../../types/routeViewModel';
import { useCreateAllocationsMutation, useUpsertRouteStopsMutation } from '../../../store/api/routesApi';
import { MutationReasonField } from '../../mutationWorkspace/MutationReasonField';
import { stopLabel, viewStopToDraft } from '../../mutationWorkspace/mutationStopHelpers';
import type { CorrectionContext } from '../hooks/useCorrectionContext';
import { mapRejectMessage } from '../../mutationWorkspace/buildMutationPolicyPreview';
import { buildAdditionalUnloadAllocationPayload } from '../utils/buildAdditionalUnloadAllocationPayload';

const { Text } = Typography;

type Props = {
  routeId: number;
  view: RouteViewModel;
  ctx: CorrectionContext;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

type SplitRow = {
  allocationId: number;
  cargoId: number;
  cargoName: string;
  pendingQty: number;
  keepQty: number;
  moveQty: number;
  fromStopId?: number;
  originalToStopId?: number;
};

export function AddUnloadWizard({ routeId, view, ctx, open, onClose, onSuccess }: Props) {
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [parentLoadingStopId, setParentLoadingStopId] = useState<number | undefined>();
  const [splits, setSplits] = useState<SplitRow[]>([]);
  const [mutationReason, setMutationReason] = useState<string | null>(null);

  const [createAllocations, { isLoading: savingAlloc }] = useCreateAllocationsMutation();
  const [upsertStops, { isLoading: savingStops }] = useUpsertRouteStopsMutation();
  const saving = savingAlloc || savingStops;

  const loadingStops = useMemo(() => {
    return (view.structure?.stops ?? [])
      .map((s, i) => viewStopToDraft(s, i))
      .filter((s) => s.type === 'loading' && s.id != null);
  }, [view.structure?.stops]);

  const pendingAllocations = useMemo((): SplitRow[] => {
    const allocations = view.structure?.allocations ?? [];
    const rows: SplitRow[] = [];
    for (const raw of allocations) {
      const a = raw as {
        id?: number;
        cargoId?: number;
        allocatedQuantity?: number;
        deliveredQuantity?: number;
        fromStopId?: number;
        toStopId?: number;
        cargo?: { name?: string };
      };
      const total = Number(a.allocatedQuantity ?? 0);
      const delivered = Number(a.deliveredQuantity ?? 0);
      const pending = Math.max(0, total - delivered);
      if (pending <= 0 || a.cargoId == null) continue;
      rows.push({
        allocationId: Number(a.id ?? 0),
        cargoId: Number(a.cargoId),
        cargoName: String(a.cargo?.name ?? `Груз #${a.cargoId}`),
        pendingQty: pending,
        keepQty: pending,
        moveQty: 0,
        fromStopId: a.fromStopId != null ? Number(a.fromStopId) : undefined,
        originalToStopId: a.toStopId != null ? Number(a.toStopId) : undefined
      });
    }
    return rows;
  }, [view.structure?.allocations]);

  const reset = () => {
    setAddress('');
    setCity('');
    setParentLoadingStopId(undefined);
    setSplits([]);
    setMutationReason(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const initSplits = () => {
    setSplits(pendingAllocations);
  };

  const handleSubmit = async () => {
    if (!mutationReason || !address.trim() || parentLoadingStopId == null) {
      message.error('Заполните адрес, родительскую погрузку и причину');
      return;
    }

    const activeSplits = splits.filter((s) => s.moveQty > 0);
    if (activeSplits.length === 0) {
      message.error('Укажите количество для переноса хотя бы по одной позиции');
      return;
    }

    const futureStops = (view.structure?.stops ?? []).map((s, i) => {
      const draft = viewStopToDraft(s, i);
      return {
        id: draft.id ?? undefined,
        type: draft.type,
        position: draft.position,
        pointType: draft.pointType,
        pointId: draft.pointId,
        parentLoadingStopId: draft.parentLoadingStopId,
        address: draft.address,
        city: draft.city,
        contactPerson: draft.contactPerson,
        phone: draft.phone,
        plannedAt: draft.plannedAt,
        notes: draft.notes
      };
    });

    const maxPos = futureStops.reduce((m, s) => Math.max(m, Number(s.position ?? 0)), -1);
    const newUnloadPosition = maxPos + 1;

    const stopsPayload = [
      ...futureStops,
      {
        type: 'unloading',
        position: newUnloadPosition,
        parentLoadingStopId,
        pointType: 'application',
        pointId: parentLoadingStopId,
        address: address.trim(),
        city: city.trim() || undefined
      }
    ];

    const existingAllocations = (view.structure?.allocations ?? []).map((raw) => {
      const a = raw as {
        id?: number;
        cargoId?: number;
        allocatedQuantity?: number;
        deliveredQuantity?: number;
        fromStopId?: number;
        toStopId?: number;
      };
      return {
        id: a.id,
        cargoId: Number(a.cargoId ?? 0),
        allocatedQuantity: Number(a.allocatedQuantity ?? 0),
        deliveredQuantity: Number(a.deliveredQuantity ?? 0),
        fromStopId: a.fromStopId,
        toStopId: a.toStopId
      };
    });

    const allocationItems = buildAdditionalUnloadAllocationPayload({
      existingAllocations,
      splits
    });

    try {
      const savedStops = await upsertStops({
        routeId,
        stops: stopsPayload,
        mutationReason,
        expectedUpdatedAt: ctx.routeUpdatedAt,
        confirmationProvided: true
      }).unwrap();

      const newUnload = [...savedStops]
        .sort((a, b) => Number(b.position) - Number(a.position))
        .find((s) => String((s as { type?: string }).type) === 'unloading');
      const newUnloadId = newUnload ? Number((newUnload as { id: number }).id) : undefined;

      const mappedAllocations = allocationItems.map((item) =>
        item.toStopId == null && newUnloadId != null ? { ...item, toStopId: newUnloadId } : item
      );

      await createAllocations({
        routeId,
        allocations: mappedAllocations,
        mutationReason,
        confirmationProvided: true
      }).unwrap();
      message.success('Выгрузка добавлена, аллокации обновлены');
      handleClose();
      onSuccess();
    } catch (e: unknown) {
      const err = e as { data?: unknown; status?: number };
      const msg =
        err.status === 409
          ? 'Маршрут был изменён. Обновите карточку и повторите.'
          : mapRejectMessage(err.data) ?? 'Не удалось сохранить выгрузку';
      message.error(msg);
    }
  };

  const columns = [
    { title: 'Груз', dataIndex: 'cargoName', key: 'name' },
    { title: 'Pending', dataIndex: 'pendingQty', key: 'pending' },
    {
      title: 'Оставить',
      key: 'keep',
      render: (_: unknown, row: SplitRow, idx: number) => (
        <InputNumber
          min={0}
          max={row.pendingQty}
          value={row.keepQty}
          onChange={(v) => {
            const keep = Number(v) || 0;
            setSplits((prev) =>
              prev.map((r, i) =>
                i === idx ? { ...r, keepQty: keep, moveQty: Math.max(0, r.pendingQty - keep) } : r
              )
            );
          }}
        />
      )
    },
    {
      title: 'На новую выгрузку',
      dataIndex: 'moveQty',
      key: 'move'
    }
  ];

  return (
    <Modal
      title="Добавить выгрузку"
      open={open}
      onCancel={handleClose}
      afterOpenChange={(visible) => {
        if (visible) initSplits();
      }}
      width={640}
      footer={
        <Space>
          <Button onClick={handleClose}>Отмена</Button>
          <Button type="primary" loading={saving} onClick={handleSubmit}>
            Сохранить
          </Button>
        </Space>
      }
    >
      <Alert
        type="info"
        showIcon
        message="Переносятся только pending-количества. Исполненные факты не изменяются."
        style={{ marginBottom: 16 }}
      />

      <Form layout="vertical">
        <Form.Item label="Родительская погрузка" required>
          <Select
            placeholder="Выберите погрузку"
            value={parentLoadingStopId}
            onChange={setParentLoadingStopId}
            options={loadingStops.map((s) => ({
              value: Number(s.id),
              label: stopLabel(s)
            }))}
          />
        </Form.Item>
        <Form.Item label="Адрес выгрузки" required>
          <Input value={address} onChange={(e) => setAddress(e.target.value)} />
        </Form.Item>
        <Form.Item label="Город">
          <Input value={city} onChange={(e) => setCity(e.target.value)} />
        </Form.Item>
      </Form>

      {pendingAllocations.length === 0 ? (
        <Text type="secondary">Нет pending-аллокаций для перераспределения.</Text>
      ) : (
        <Table size="small" rowKey="allocationId" pagination={false} dataSource={splits} columns={columns} />
      )}

      <div style={{ marginTop: 16 }}>
        <MutationReasonField value={mutationReason} onChange={setMutationReason} disabled={saving} />
      </div>
    </Modal>
  );
}
